import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiTrendingUp,
  FiAward,
  FiTarget,
  FiBarChart2,
  FiCalendar,
  FiClock,
  FiZap,
  FiHexagon,
  FiStar,
  FiActivity,
  FiRefreshCw,
  FiBookOpen,
  FiPercent,
  FiArrowLeft,
  FiShield,
  FiHeart,
  FiBook,
  FiGift,
  FiCheckCircle,
} from 'react-icons/fi';
import '../GRE.css';
import InfoTooltip from '../../../components/InfoTooltip';
import { getComprehensiveQuantProgress } from './api/progressAPI';
import { API_BASE_URL, redirectToLoginWithReturn } from '../../../utils/utils';
import { getErrorMessage } from '../../../utils/getErrorMessage';

const QuantProgress = () => {
  const navigate = useNavigate();
  const authRedirectStartedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });

  // Get user email from session storage or URL params
  const [user, setUser] = useState(() => {
    try {
      const storedUser = sessionStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error('Error parsing user from sessionStorage:', error);
      return null;
    }
  });

  const [userEmail, setUserEmail] = useState(null);

  const redirectToLogin = (
    message = 'Votre session a expire. Veuillez vous reconnecter.'
  ) => {
    if (authRedirectStartedRef.current) {
      return;
    }

    authRedirectStartedRef.current = true;
    redirectToLoginWithReturn({
      navigate,
      redirectTo: window.location.pathname + window.location.search,
      message,
    });
  };

  // Set userEmail from URL params, user object, or localStorage fallback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlUserEmail = urlParams.get('user_email');

    if (urlUserEmail) {
      setUserEmail(urlUserEmail);
    } else if (user?.email) {
      setUserEmail(user.email);
    } else {
      const storedEmail = localStorage.getItem('userEmail');
      if (storedEmail) {
        setUserEmail(storedEmail);
      }
    }
  }, [user]);

  // Ref to track ongoing API requests to prevent duplicates
  const ongoingRequests = useRef({});

  const loadProgressData = async (forceRefresh = false) => {
    if (!userEmail) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch comprehensive quantitative progress data from backend
      const progressResponse = await getComprehensiveQuantProgress(userEmail);

      console.log('Quantitative progress response:', progressResponse);

      if (progressResponse && progressResponse.overall_summary) {
        setProgressData(progressResponse);
      } else {
        throw new Error('No quantitative progress data available');
      }
    } catch (error) {
      console.error('Error loading quantitative progress data:', error);
      setError(error.message);

      if (error.message === 'Authentication failed') {
        redirectToLogin();
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userEmail) {
      loadProgressData();
    }
  }, [userEmail]);

  const displayToast = (toastState) => {
    if (!toastState.show) return null;

    return (
      <div
        className={`toast-notification ${toastState.success ? 'success' : 'error'}`}
        style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          zIndex: 9999,
        }}
      >
        <div className="toast-icon">{toastState.success ? '✓' : '⚠'}</div>
        {toastState.message}
      </div>
    );
  };

  // Gamification Helper Functions
  const calculateXP = (recentSessions) => {
    return recentSessions.reduce((total, session) => {
      let sessionXP = 0;
      // Base XP for completion
      sessionXP += 10;
      // Bonus XP based on score
      if (session.score_percentage >= 90)
        sessionXP += 25; // Mastery bonus
      else if (session.score_percentage >= 70)
        sessionXP += 15; // Completion bonus
      else if (session.score_percentage >= 50) sessionXP += 5; // Effort bonus

      return total + sessionXP;
    }, 0);
  };

  const calculateLevel = (xp) => {
    return Math.floor(xp / 100) + 1; // 100 XP per level
  };

  const getXPForNextLevel = (xp) => {
    const currentLevel = calculateLevel(xp);
    const nextLevelXP = currentLevel * 100;
    return nextLevelXP - xp;
  };

  const calculateStreak = (recentSessions) => {
    if (recentSessions.length === 0) return 0;

    const today = new Date();
    let streak = 0;
    const sessionDates = recentSessions
      .map((session) => new Date(session.completed_at).toDateString())
      .filter((date, index, arr) => arr.indexOf(date) === index) // Remove duplicates
      .sort((a, b) => new Date(b) - new Date(a)); // Sort newest first

    for (let i = 0; i < sessionDates.length; i++) {
      const sessionDate = new Date(sessionDates[i]);
      const daysDiff = Math.floor(
        (today - sessionDate) / (1000 * 60 * 60 * 24)
      );

      if (daysDiff === i) {
        streak++;
      } else {
        break;
      }
    }

    return streak;
  };

  const getAchievements = (overallSummary, recentSessions, xp) => {
    const achievements = [];
    const completedLessons = overallSummary.completed_lessons || 0;
    const totalLessons = overallSummary.total_lessons || 15;
    const averageScore = overallSummary.average_score || 0;
    const totalSessions = recentSessions.length;
    const perfectScores = recentSessions.filter(
      (s) => s.score_percentage === 100
    ).length;
    const streak = overallSummary.current_streak_days || 0;

    // Achievement definitions
    const achievementsList = [
      {
        id: 'first_lesson',
        title: 'First Steps',
        description: 'Complete your first lesson',
        icon: 'FiStar',
        earned: completedLessons >= 1,
        color: 'var(--liwaza-success)',
      },
      {
        id: 'five_lessons',
        title: 'Getting Warmed Up',
        description: 'Complete 5 lessons',
        icon: 'FiZap',
        earned: completedLessons >= 5,
        color: 'var(--liwaza-warning)',
      },
      {
        id: 'all_lessons',
        title: 'Master Student',
        description: 'Complete all 15 lessons',
        icon: 'FiHexagon',
        earned: completedLessons >= 15,
        color: 'var(--liwaza-tertiary)',
      },
      {
        id: 'first_mastery',
        title: 'Perfectionist',
        description: 'Master your first lesson (90%+)',
        icon: 'FiStar',
        earned: overallSummary.best_score >= 90,
        color: 'var(--liwaza-success)',
      },
      {
        id: 'five_mastery',
        title: 'Excellence',
        description: 'Master 5 lessons',
        icon: 'FiShield',
        earned: overallSummary.best_score >= 90,
        color: 'var(--liwaza-primary)',
      },
      {
        id: 'perfect_score',
        title: 'Flawless Victory',
        description: 'Score 100% on a practice',
        icon: 'FiZap',
        earned: perfectScores >= 1,
        color: 'var(--liwaza-warning)',
      },
      {
        id: 'ten_sessions',
        title: 'Dedicated Learner',
        description: 'Complete 10 practice sessions',
        icon: 'FiHeart',
        earned: totalSessions >= 10,
        color: 'var(--liwaza-secondary)',
      },
      {
        id: 'three_day_streak',
        title: 'On Fire!',
        description: 'Practice for 3 days in a row',
        icon: 'FiZap',
        earned: streak >= 3,
        color: 'var(--liwaza-warning)',
      },
      {
        id: 'level_5',
        title: 'Rising Star',
        description: 'Reach Level 5',
        icon: 'FiStar',
        earned: calculateLevel(xp) >= 5,
        color: 'var(--liwaza-tertiary)',
      },
      {
        id: 'high_average',
        title: 'Consistent Performer',
        description: 'Maintain 80%+ average score',
        icon: 'FiTarget',
        earned: averageScore > 0 && averageScore >= 80,
        color: 'var(--liwaza-success)',
      },
    ];

    return achievementsList.filter((achievement) => achievement.earned);
  };

  // Helper function to render icons based on string names
  const renderIcon = (iconName, style = {}) => {
    const iconMap = {
      FiStar: FiStar,
      FiZap: FiZap,
      FiHexagon: FiHexagon,
      FiShield: FiShield,
      FiHeart: FiHeart,
      FiTarget: FiTarget,
      FiBook: FiBook,
      FiGift: FiGift,
      FiTrendingUp: FiTrendingUp,
    };

    const IconComponent = iconMap[iconName];
    return IconComponent ? (
      <IconComponent style={style} />
    ) : (
      <FiStar style={style} />
    );
  };

  const getStatusColor = (score) => {
    if (score >= 90) return 'var(--liwaza-success)'; // Mastered - Success green
    if (score >= 70) return 'var(--liwaza-tertiary)'; // Completed - Tertiary color
    if (score > 0) return 'var(--liwaza-warning)'; // Attempted - Warning orange
    return 'var(--liwaza-outline)'; // Not started - Gray
  };

  const getStatusColorWithOpacity = (score) => {
    if (score >= 90) return 'var(--liwaza-success-container)'; // Mastered - Light green container
    if (score >= 70) return 'var(--liwaza-tertiary-container)'; // Completed - Light purple container
    if (score > 0) return 'var(--liwaza-warning-container)'; // Attempted - Light orange container
    return 'var(--liwaza-outline-variant)'; // Not started - Light gray container
  };

  const getStatusText = (isCompleted, score) => {
    if (isCompleted && score >= 90) return 'Mastered';
    if (isCompleted && score >= 70) return 'Completed';
    if (score > 0 && score < 70) return 'Needs Practice';
    if (score > 0) return 'Attempted';
    return 'Not Started';
  };

  if (loading) {
    return (
      <div
        style={{
          padding: '2rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid var(--liwaza-outline-variant)',
              borderTop: '4px solid var(--liwaza-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem',
            }}
          />
          <p style={{ color: 'var(--liwaza-on-surface-variant)' }}>
            Loading progress data...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div
          style={{
            background: 'var(--liwaza-error-container)',
            color: 'var(--liwaza-on-error-container)',
            padding: '2rem',
            borderRadius: '12px',
            marginBottom: '2rem',
          }}
        >
          <h3>Error Loading Progress</h3>
          <p>{getErrorMessage(error, 'Failed to load progress data.')}</p>
          <button
            onClick={() => loadProgressData(true)}
            style={{
              background: 'var(--liwaza-error)',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              marginTop: '1rem',
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const overallSummary = progressData?.overall_summary || {};
  const recentSessions = progressData?.recent_sessions || [];
  const performanceTrends = progressData?.performance_trends || {};
  const dailyProgression =
    progressData?.timeline_progression?.daily_progression || [];
  const lessonStatistics = progressData?.lesson_statistics || {};

  console.log('=== Quant Progress Debug ===');
  console.log('Overall Summary:', overallSummary);
  console.log('Lesson Statistics:', lessonStatistics);
  console.log('Recent Sessions:', recentSessions);

  // Calculate stats from lesson_statistics if available
  let completedLessonsFromStats = 0;
  let totalScoreFromStats = 0;
  let lessonsWithScores = 0;
  let totalStudyMinutesFromStats = 0;

  if (lessonStatistics && Object.keys(lessonStatistics).length > 0) {
    Object.values(lessonStatistics).forEach((stats) => {
      if (stats.is_completed) completedLessonsFromStats++;
      if (stats.best_score > 0) {
        totalScoreFromStats += stats.best_score;
        lessonsWithScores++;
      }
      totalStudyMinutesFromStats += stats.total_time_minutes || 0;
    });
  }

  // Also calculate from localStorage as fallback
  let localCompletedCount = 0;
  let localTotalScore = 0;
  let localLessonsWithScores = 0;
  let localTotalMinutes = 0;

  try {
    const localProgress = JSON.parse(
      localStorage.getItem('concepts_mastery_progress') || '[]'
    );
    localProgress.forEach((lesson) => {
      if (lesson.score >= 70) localCompletedCount++;
      if (lesson.score > 0) {
        localTotalScore += lesson.score;
        localLessonsWithScores++;
        localTotalMinutes += (lesson.timeSpent || 0) / 1000 / 60; // Convert ms to minutes
      }
    });
  } catch (error) {
    console.error('Error reading localStorage:', error);
  }

  console.log('Stats from lesson_statistics:', {
    completed: completedLessonsFromStats,
    averageScore:
      lessonsWithScores > 0 ? totalScoreFromStats / lessonsWithScores : 0,
    totalMinutes: totalStudyMinutesFromStats,
  });

  console.log('Stats from localStorage:', {
    completed: localCompletedCount,
    averageScore:
      localLessonsWithScores > 0 ? localTotalScore / localLessonsWithScores : 0,
    totalMinutes: localTotalMinutes,
  });

  // Extract statistics with smart fallbacks
  const completedLessonsCount =
    overallSummary.completed_lessons ||
    completedLessonsFromStats ||
    localCompletedCount ||
    0;

  const totalLessonsCount = 15;

  const accurateCompletionRate =
    overallSummary.completion_rate ||
    Math.round((completedLessonsCount / totalLessonsCount) * 100);

  const calculatedAverageFromStats =
    lessonsWithScores > 0
      ? Math.round(totalScoreFromStats / lessonsWithScores)
      : 0;
  const calculatedAverageFromLocal =
    localLessonsWithScores > 0
      ? Math.round(localTotalScore / localLessonsWithScores)
      : 0;

  const accurateAverageScore =
    overallSummary.average_score ||
    calculatedAverageFromStats ||
    calculatedAverageFromLocal ||
    0;

  const totalPracticeSessions =
    overallSummary.total_practice_sessions || recentSessions.length || 0;

  const calculatedStudyTimeFromStats = Math.round(
    totalStudyMinutesFromStats / 60
  );
  const calculatedStudyTimeFromLocal = Math.round(localTotalMinutes / 60);

  // Fix study time - check if backend value is > 0 before using it
  const backendStudyTimeHours = Math.round(
    (overallSummary.total_study_time_minutes || 0) / 60
  );
  const totalStudyTimeHours =
    backendStudyTimeHours > 0
      ? backendStudyTimeHours
      : calculatedStudyTimeFromStats > 0
        ? calculatedStudyTimeFromStats
        : calculatedStudyTimeFromLocal > 0
          ? calculatedStudyTimeFromLocal
          : 0;

  // Calculate readiness score if backend doesn't provide it
  const calculateReadinessScore = () => {
    // Readiness is based on:
    // 40% - Completion rate (how many lessons completed)
    // 40% - Average score (how well you're doing)
    // 20% - Practice consistency (sessions completed)

    const completionComponent =
      (completedLessonsCount / totalLessonsCount) * 40;
    const scoreComponent = (accurateAverageScore / 100) * 40;
    const practiceComponent = Math.min(totalPracticeSessions / 20, 1) * 20; // Max 20 points if 20+ sessions

    return Math.round(completionComponent + scoreComponent + practiceComponent);
  };

  const readinessScore =
    overallSummary.readiness_score && overallSummary.readiness_score > 0
      ? overallSummary.readiness_score
      : calculateReadinessScore();
  const currentStreakDays = overallSummary.current_streak_days || 0;
  const longestStreakDays = overallSummary.longest_streak_days || 0;

  console.log('Study Time Breakdown:', {
    backendMinutes: overallSummary.total_study_time_minutes,
    backendHours: backendStudyTimeHours,
    statsMinutes: totalStudyMinutesFromStats,
    statsHours: calculatedStudyTimeFromStats,
    localMinutes: localTotalMinutes,
    localHours: calculatedStudyTimeFromLocal,
    finalHours: totalStudyTimeHours,
  });

  console.log('Final values being displayed:', {
    completedLessonsCount,
    totalLessonsCount,
    accurateAverageScore,
    totalPracticeSessions,
    totalStudyTimeHours,
    readinessScore,
    calculatedReadinessScore: calculateReadinessScore(),
  });
  console.log('=== End Debug ===');

  // Gamification calculations using new data
  const totalXP = calculateXP(recentSessions);
  const currentLevel = calculateLevel(totalXP);
  const xpForNextLevel = getXPForNextLevel(totalXP);
  const currentStreak = currentStreakDays; // Use backend calculated streak
  const achievements = getAchievements(overallSummary, recentSessions, totalXP);

  console.log('Processed data state:', {
    progressDataExists: !!progressData,
    completedLessonsCount,
    totalLessonsCount,
    readinessScore,
    recentSessionsCount: recentSessions.length,
    totalXP,
    currentLevel,
    currentStreak,
    achievementsCount: achievements.length,
    totalPracticeSessions,
    totalStudyTimeHours,
  });

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={() =>
            navigate('/exams/gre', { state: { activeCategory: 'concepts' } })
          }
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--liwaza-primary)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '1rem',
            marginBottom: '1rem',
          }}
        >
          <FiArrowLeft /> Back to Concepts
        </button>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h1
              style={{
                color: 'var(--liwaza-on-surface)',
                marginBottom: '0.5rem',
              }}
            >
              Quantitative Progress
            </h1>
            <p
              style={{
                color: 'var(--liwaza-on-surface-variant)',
                fontSize: '1.1rem',
              }}
            >
              Track your GRE Quant preparation progress
            </p>
          </div>

          <button
            onClick={() => loadProgressData(true)}
            style={{
              background: 'var(--liwaza-surface-container)',
              color: 'var(--liwaza-on-surface)',
              border: '1px solid var(--liwaza-outline)',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <FiRefreshCw /> Refresh
          </button>
        </div>
      </div>

      {/* Overall Progress Card */}
      <div
        style={{
          background: 'var(--liwaza-surface-container)',
          padding: '2rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '1px solid var(--liwaza-outline-variant)',
        }}
      >
        <h2
          style={{
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'var(--liwaza-on-surface)',
          }}
        >
          <FiBarChart2 /> Overall Progress
        </h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '2rem',
          }}
        >
          {/* Level & XP */}
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '80px',
                height: '80px',
                border: `4px solid var(--liwaza-primary)`,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                background: `var(--liwaza-primary)20`,
                position: 'relative',
              }}
            >
              <span
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: 'var(--liwaza-primary)',
                }}
              >
                {currentLevel}
              </span>
              {renderIcon('FiHexagon', {
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                background: 'var(--liwaza-warning)',
                color: 'white',
                padding: '4px',
                borderRadius: '50%',
                fontSize: '12px',
              })}
            </div>
            <h3
              style={{
                margin: '0 0 0.5rem',
                color: 'var(--liwaza-on-surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
              }}
            >
              Level {currentLevel}
              <InfoTooltip
                content="Your current level in GRE Quant preparation, based on XP earned from practice sessions. Each level represents your progress and commitment. Level up by practicing more and mastering lessons."
                position="top"
                maxWidth="280px"
              />
            </h3>
            <div
              style={{
                margin: 0,
                color: 'var(--liwaza-on-surface-variant)',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
              }}
            >
              {totalXP} XP
              <InfoTooltip
                content="Experience Points (XP) are earned for every practice session. More XP means more practice and progress. Earn XP by completing lessons, achieving high scores, and maintaining streaks."
                position="top"
                maxWidth="260px"
              />
              • {xpForNextLevel} to next level
            </div>
          </div>

          {/* Streak */}
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color:
                  currentStreak >= 3
                    ? 'var(--liwaza-warning)'
                    : 'var(--liwaza-secondary)',
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
              }}
            >
              {renderIcon('FiZap', {
                color:
                  currentStreak >= 3
                    ? 'var(--liwaza-warning)'
                    : 'var(--liwaza-outline)',
              })}
              {currentStreak}
            </div>
            <h3
              style={{
                margin: '0 0 0.5rem',
                color: 'var(--liwaza-on-surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
              }}
            >
              Day Streak
              <InfoTooltip
                content="Number of consecutive days you've practiced quantitative problems. Consistent daily practice helps build mathematical intuition and improves problem-solving speed. Maintain a 7+ day streak for optimal learning."
                position="bottom"
                maxWidth="280px"
              />
            </h3>
            <div
              style={{
                margin: 0,
                color: 'var(--liwaza-on-surface-variant)',
                fontSize: '0.9rem',
              }}
            >
              {currentStreak >= 3 ? 'On fire! 🔥' : 'Keep practicing!'}
            </div>
          </div>

          {/* Readiness Score */}
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '80px',
                height: '80px',
                border: `4px solid ${getStatusColor(readinessScore)}`,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                background: `${getStatusColor(readinessScore)}20`,
              }}
            >
              <span
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: getStatusColor(readinessScore),
                }}
              >
                {readinessScore}
              </span>
            </div>
            <h3
              style={{
                margin: '0 0 0.5rem',
                color: 'var(--liwaza-on-surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
              }}
            >
              Readiness Score
              <InfoTooltip
                content="A score out of 100 that estimates your overall readiness for the GRE Quant section. Calculated from lesson completion, average score, and practice frequency. Aim for 80+ to be exam-ready."
                position="top"
                maxWidth="260px"
              />
            </h3>
            <div
              style={{
                margin: 0,
                color: 'var(--liwaza-on-surface-variant)',
                fontSize: '0.9rem',
              }}
            >
              Out of 100
            </div>
          </div>

          {/* Lessons Completed */}
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: 'var(--liwaza-primary)',
                marginBottom: '0.5rem',
              }}
            >
              {completedLessonsCount}/{totalLessonsCount}
            </div>
            <h3
              style={{
                margin: '0 0 0.5rem',
                color: 'var(--liwaza-on-surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
              }}
            >
              Lessons Completed
              <InfoTooltip
                content="Number of quant lessons you have completed. Completing more lessons means broader topic coverage and better exam preparation."
                position="top"
                maxWidth="260px"
              />
            </h3>
            <div
              style={{
                margin: 0,
                color: 'var(--liwaza-on-surface-variant)',
                fontSize: '0.9rem',
              }}
            >
              {Math.round(accurateCompletionRate)}% Complete
            </div>
          </div>

          {/* Average Score */}
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: 'var(--liwaza-secondary)',
                marginBottom: '0.5rem',
              }}
            >
              {Math.round(accurateAverageScore)}%
            </div>
            <h3
              style={{
                margin: '0 0 0.5rem',
                color: 'var(--liwaza-on-surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
              }}
            >
              Average Score
              <InfoTooltip
                content="Your average score across all quant practice sessions. Higher averages mean stronger problem-solving skills. Aim for 80%+ for top performance."
                position="top"
                maxWidth="260px"
              />
            </h3>
            <div
              style={{
                margin: 0,
                color: 'var(--liwaza-on-surface-variant)',
                fontSize: '0.9rem',
              }}
            >
              Across all practices
            </div>
          </div>

          {/* Total Study Time */}
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: 'var(--liwaza-info)',
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
              }}
            >
              <FiClock />
              {totalStudyTimeHours}h
            </div>
            <h3
              style={{
                margin: '0 0 0.5rem',
                color: 'var(--liwaza-on-surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
              }}
            >
              Study Time
              <InfoTooltip
                content="Total time spent studying quantitative concepts and practicing problems. Consistent study time leads to better performance on the actual GRE."
                position="top"
                maxWidth="260px"
              />
            </h3>
            <div
              style={{
                margin: 0,
                color: 'var(--liwaza-on-surface-variant)',
                fontSize: '0.9rem',
              }}
            >
              Total hours practiced
            </div>
          </div>

          {/* Practice Sessions */}
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: 'var(--liwaza-warning)',
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
              }}
            >
              <FiTarget />
              {totalPracticeSessions}
            </div>
            <h3
              style={{
                margin: '0 0 0.5rem',
                color: 'var(--liwaza-on-surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
              }}
            >
              Practice Sessions
              <InfoTooltip
                content="Number of practice sessions completed. More practice sessions indicate consistent preparation and better familiarity with problem types."
                position="top"
                maxWidth="260px"
              />
            </h3>
            <div
              style={{
                margin: 0,
                color: 'var(--liwaza-on-surface-variant)',
                fontSize: '0.9rem',
              }}
            >
              Sessions completed
            </div>
          </div>

          {/* Achievements */}
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: 'var(--liwaza-tertiary)',
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
              }}
            >
              {renderIcon('FiHexagon')}
              {achievements.length}
            </div>
            <h3
              style={{
                margin: '0 0 0.5rem',
                color: 'var(--liwaza-on-surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
              }}
            >
              Achievements
              <InfoTooltip
                content="Badges earned for reaching milestones in your GRE Quant journey. Collect more achievements by mastering lessons, maintaining streaks, and scoring high."
                position="top"
                maxWidth="260px"
              />
            </h3>
            <div
              style={{
                margin: 0,
                color: 'var(--liwaza-on-surface-variant)',
                fontSize: '0.9rem',
              }}
            >
              Badges earned
            </div>
          </div>
        </div>
      </div>

      {/* Achievements Section */}
      {achievements.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h2
            style={{
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--liwaza-on-surface)',
            }}
          >
            {renderIcon('FiHexagon')} Achievements Unlocked
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem',
            }}
          >
            {achievements.map((achievement) => (
              <div
                key={achievement.id}
                style={{
                  background: 'var(--liwaza-surface-container)',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  border: `2px solid ${achievement.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  animation:
                    'achievementPulse 2s ease-in-out infinite alternate',
                }}
              >
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    background: achievement.color,
                    color: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    flexShrink: 0,
                  }}
                >
                  {renderIcon(achievement.icon)}
                </div>

                <div>
                  <h4
                    style={{
                      margin: '0 0 0.25rem',
                      color: 'var(--liwaza-on-surface)',
                    }}
                  >
                    {achievement.title}
                  </h4>
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--liwaza-on-surface-variant)',
                      fontSize: '0.9rem',
                    }}
                  >
                    {achievement.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekly Progression Analysis */}
      <div style={{ marginBottom: '2rem' }}>
        <h2
          style={{
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'var(--liwaza-on-surface)',
          }}
        >
          <FiTrendingUp /> Weekly Progression
        </h2>

        {/* Weekly Performance Trends */}
        {performanceTrends.weekly_performance &&
          performanceTrends.weekly_performance.length > 0 && (
            <div
              style={{
                background: 'var(--liwaza-surface-container)',
                padding: '1.5rem',
                borderRadius: '12px',
                marginBottom: '1.5rem',
                border: '1px solid var(--liwaza-outline-variant)',
              }}
            >
              <h3
                style={{
                  marginBottom: '1rem',
                  color: 'var(--liwaza-on-surface)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FiBarChart2 /> Weekly Performance Summary
              </h3>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1rem',
                }}
              >
                {performanceTrends.weekly_performance.map((week, index) => {
                  const prevWeek =
                    index > 0
                      ? performanceTrends.weekly_performance[index - 1]
                      : null;
                  const scoreChange = prevWeek
                    ? week.average_score - prevWeek.average_score
                    : 0;
                  const sessionChange = prevWeek
                    ? week.session_count - prevWeek.session_count
                    : 0;

                  const weekDate = new Date(week.week);
                  const weekLabel = `Week of ${weekDate.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;

                  return (
                    <div
                      key={week.week}
                      style={{
                        background: 'var(--liwaza-surface-container-highest)',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid var(--liwaza-outline-variant)',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          color: 'var(--liwaza-on-surface)',
                          marginBottom: '0.5rem',
                        }}
                      >
                        {weekLabel}
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.5rem',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            color: 'var(--liwaza-primary)',
                          }}
                        >
                          {Math.round(week.average_score)}%
                        </span>
                        {scoreChange !== 0 && (
                          <span
                            style={{
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              color:
                                scoreChange > 0
                                  ? 'var(--liwaza-success)'
                                  : 'var(--liwaza-error)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                            }}
                          >
                            {scoreChange > 0 ? '↗' : '↘'}{' '}
                            {Math.abs(scoreChange).toFixed(1)}%
                          </span>
                        )}
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '0.8rem',
                          color: 'var(--liwaza-on-surface-variant)',
                        }}
                      >
                        <span>{week.session_count} sessions</span>
                        {sessionChange !== 0 && (
                          <span
                            style={{
                              color:
                                sessionChange > 0
                                  ? 'var(--liwaza-success)'
                                  : 'var(--liwaza-warning)',
                            }}
                          >
                            {sessionChange > 0 ? '+' : ''}
                            {sessionChange}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        {/* Daily Progression (Last 2 Weeks) */}
        {dailyProgression.length > 0 && (
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: '1.5rem',
              borderRadius: '12px',
              border: '1px solid var(--liwaza-outline-variant)',
            }}
          >
            <h3
              style={{
                marginBottom: '1rem',
                color: 'var(--liwaza-on-surface)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <FiActivity /> Daily Progress (Last {dailyProgression.length}{' '}
              Days)
            </h3>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: '0.5rem',
                marginBottom: '1rem',
              }}
            >
              {dailyProgression
                .slice(-dailyProgression.length)
                .map((day, index) => {
                  const dayDate = new Date(day.date);
                  const isToday =
                    dayDate.toDateString() === new Date().toDateString();
                  const hasActivity = day.sessions_completed > 0;

                  return (
                    <div
                      key={day.date}
                      style={{
                        background: hasActivity
                          ? isToday
                            ? 'var(--liwaza-primary-container)'
                            : 'var(--liwaza-surface-container-highest)'
                          : 'var(--liwaza-outline-variant)',
                        padding: '0.75rem 0.5rem',
                        borderRadius: '6px',
                        textAlign: 'center',
                        border: isToday
                          ? '2px solid var(--liwaza-primary)'
                          : '1px solid transparent',
                        opacity: hasActivity ? 1 : 0.6,
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.7rem',
                          color: hasActivity
                            ? 'var(--liwaza-on-surface-variant)'
                            : 'var(--liwaza-on-surface-variant)',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {dayDate.toLocaleDateString([], {
                          weekday: 'short',
                          month: 'numeric',
                          day: 'numeric',
                        })}
                      </div>

                      {hasActivity ? (
                        <>
                          <div
                            style={{
                              fontSize: '1rem',
                              fontWeight: 'bold',
                              color: 'var(--liwaza-primary)',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {Math.round(day.avg_score)}%
                          </div>
                          <div
                            style={{
                              fontSize: '0.7rem',
                              color: 'var(--liwaza-on-surface-variant)',
                            }}
                          >
                            {day.sessions_completed} session
                            {day.sessions_completed !== 1 ? 's' : ''}
                          </div>
                          <div
                            style={{
                              fontSize: '0.7rem',
                              color: 'var(--liwaza-on-surface-variant)',
                            }}
                          >
                            {day.total_time_minutes}min
                          </div>
                        </>
                      ) : (
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: 'var(--liwaza-on-surface-variant)',
                            opacity: 0.7,
                          }}
                        >
                          No practice
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            {/* Weekly Summary Stats */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '1rem',
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid var(--liwaza-outline-variant)',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: 'var(--liwaza-primary)',
                  }}
                >
                  {
                    dailyProgression.filter((d) => d.sessions_completed > 0)
                      .length
                  }
                </div>
                <div
                  style={{
                    fontSize: '0.8rem',
                    color: 'var(--liwaza-on-surface-variant)',
                  }}
                >
                  Active Days
                </div>
              </div>

              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: 'var(--liwaza-secondary)',
                  }}
                >
                  {Math.round(
                    dailyProgression
                      .filter((d) => d.sessions_completed > 0)
                      .reduce((sum, d) => sum + d.avg_score, 0) /
                      (dailyProgression.filter((d) => d.sessions_completed > 0)
                        .length || 1)
                  )}
                  %
                </div>
                <div
                  style={{
                    fontSize: '0.8rem',
                    color: 'var(--liwaza-on-surface-variant)',
                  }}
                >
                  Avg Score
                </div>
              </div>

              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: 'var(--liwaza-tertiary)',
                  }}
                >
                  {dailyProgression.reduce(
                    (sum, d) => sum + d.total_questions,
                    0
                  )}
                </div>
                <div
                  style={{
                    fontSize: '0.8rem',
                    color: 'var(--liwaza-on-surface-variant)',
                  }}
                >
                  Total Questions
                </div>
              </div>

              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: 'var(--liwaza-success)',
                  }}
                >
                  {Math.round(
                    (dailyProgression.reduce(
                      (sum, d) => sum + d.total_correct,
                      0
                    ) /
                      (dailyProgression.reduce(
                        (sum, d) => sum + d.total_questions,
                        0
                      ) || 1)) *
                      100
                  )}
                  %
                </div>
                <div
                  style={{
                    fontSize: '0.8rem',
                    color: 'var(--liwaza-on-surface-variant)',
                  }}
                >
                  Overall Accuracy
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Sessions */}
      <div style={{ marginBottom: '2rem' }}>
        <h2
          style={{
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'var(--liwaza-on-surface)',
          }}
        >
          <FiActivity /> Recent Practice Sessions
        </h2>

        {recentSessions.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem',
            }}
          >
            {recentSessions.slice(0, 6).map((session, index) => {
              const scoreColor =
                session.score_percentage >= 90
                  ? 'var(--liwaza-success)'
                  : session.score_percentage >= 70
                    ? 'var(--liwaza-warning)'
                    : 'var(--liwaza-error)';

              const sessionDate = new Date(
                session.completed_at
              ).toLocaleDateString();
              const sessionTime = new Date(
                session.completed_at
              ).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              });

              return (
                <div
                  key={index}
                  style={{
                    background: 'var(--liwaza-surface-container)',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    border: `2px solid ${scoreColor}20`,
                    borderLeft: `4px solid ${scoreColor}`,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '1rem',
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        color: 'var(--liwaza-on-surface)',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                      }}
                    >
                      {session.lesson_name || session.lesson_id}
                    </h3>
                    <div
                      style={{
                        background: scoreColor,
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                      }}
                    >
                      {Math.round(session.score_percentage)}%
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1rem',
                      marginBottom: '1rem',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: 'var(--liwaza-on-surface-variant)',
                          marginBottom: '0.25rem',
                        }}
                      >
                        Mode
                      </div>
                      <div
                        style={{
                          fontSize: '0.9rem',
                          color: 'var(--liwaza-on-surface)',
                          fontWeight: 500,
                          textTransform: 'capitalize',
                        }}
                      >
                        {session.practice_mode || 'Practice'}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: 'var(--liwaza-on-surface-variant)',
                          marginBottom: '0.25rem',
                        }}
                      >
                        Duration
                      </div>
                      <div
                        style={{
                          fontSize: '0.9rem',
                          color: 'var(--liwaza-on-surface)',
                          fontWeight: 500,
                        }}
                      >
                        {Math.round((session.total_time_seconds || 0) / 60)} min
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--liwaza-on-surface-variant)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <FiCalendar size={14} />
                    {sessionDate} at {sessionTime}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: '2rem',
              borderRadius: '8px',
              textAlign: 'center',
              color: 'var(--liwaza-on-surface-variant)',
            }}
          >
            <FiActivity
              size={48}
              style={{ marginBottom: '1rem', opacity: 0.5 }}
            />
            <p style={{ margin: 0, fontSize: '1.1rem' }}>
              No practice sessions yet. Start practicing to see your progress
              here!
            </p>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      {recentSessions.length > 0 && (
        <div>
          <h2
            style={{
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--liwaza-on-surface)',
            }}
          >
            <FiClock /> Recent Activity
          </h2>

          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              borderRadius: '8px',
              border: '1px solid var(--liwaza-outline-variant)',
              overflow: 'hidden',
            }}
          >
            {recentSessions.map((session, index) => (
              <div
                key={index}
                style={{
                  padding: '1rem 1.5rem',
                  borderBottom:
                    index < recentSessions.length - 1
                      ? '1px solid var(--liwaza-outline-variant)'
                      : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <h4
                    style={{
                      margin: '0 0 0.25rem',
                      color: 'var(--liwaza-on-surface)',
                    }}
                  >
                    {session.lesson_name}
                  </h4>
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--liwaza-on-surface-variant)',
                      fontSize: '0.9rem',
                    }}
                  >
                    {session.practice_mode === 'timed'
                      ? 'Timed Practice'
                      : 'Untimed Practice'}{' '}
                    • {new Date(session.completed_at).toLocaleDateString()}
                  </p>
                </div>

                <div
                  style={{
                    background: `${getStatusColor(session.score_percentage)}20`,
                    color: getStatusColor(session.score_percentage),
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    fontWeight: '600',
                  }}
                >
                  {session.score_percentage}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {displayToast(toast)}
    </div>
  );
};

export default QuantProgress;
